import Logger from "./Logger";
import AxiosHttpClient from "./HttpClient";
import ErrorHandlingUtils from "./ErrorHandlingUtils";
const TAG = 'PermissionsManager';
export default class PermissionsManager {
    static hasAllDirectPermissions(permissions) {
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }
        if (!permissions)
            return true;
        if (permissions.length < 1)
            return true;
        const intersection = PermissionsManager.getIntersectionWithDirectPermissions(permissions);
        return intersection.size == permissions.length;
    }
    static hasOneDirectPermission(permissions) {
        if (!permissions)
            return true;
        if (permissions.length < 1)
            return true;
        const intersection = PermissionsManager.getIntersectionWithDirectPermissions(permissions);
        return intersection.size > 0;
    }
    static hasFeature(feature) {
        if (!feature)
            return true;
        if (feature.length < 1)
            return true;
        const currentPermissions = PermissionsManager.getCurrentPermissions();
        if (currentPermissions.allFeaturesEnabled) {
            return true;
        }
        if (!currentPermissions.features) {
            Logger.warn(TAG, "The user does not have any features");
            return false;
        }
        return currentPermissions.features.indexOf(feature) >= 0;
    }
    static getCurrentPermissions() {
        PermissionsManager.throttledPermissionScheduler(PermissionsManager.maybeSchedulePermissionsRefresh);
        const permissionsStr = typeof (localStorage) !== 'undefined' ? localStorage.getItem(PermissionsManager.KEY_PERMISSIONS) : 0;
        if (permissionsStr) {
            return JSON.parse(permissionsStr);
        }
        // By default return empty map
        return {};
    }
    static maybeSchedulePermissionsRefresh() {
        const permissionsLastUpdatedAtStr = typeof (localStorage) !== 'undefined' ? localStorage.getItem(PermissionsManager.KEY_PERMISSIONS_LAST_UPDATED_AT) : 0;
        if (permissionsLastUpdatedAtStr) {
            const permissionsLastUpdatedAt = Date.parse(permissionsLastUpdatedAtStr);
            const now = new Date();
            if ((now.getTime() - permissionsLastUpdatedAt) < PermissionsManager.PERMISSIONS_UPDATE_INTERVAL_MILLIS) {
                // No need to update permissions, they are up to date
                return;
            }
        }
        // If we are here, we should schedule permissions refresh
        Logger.info(TAG, "Scheduling permissions refresh...");
        const httpClient = new AxiosHttpClient(false, false);
        httpClient.get('/o/v1/permissions', PermissionsManager.saveNewPermissions, () => ErrorHandlingUtils.handleCriticalError(400));
    }
    static saveNewPermissions(newPermissions) {
        const newPermissionsStr = JSON.stringify(newPermissions);
        if (typeof (localStorage) !== 'undefined') {
            PermissionsManager.updateLocalStorageState(PermissionsManager.KEY_PERMISSIONS, newPermissionsStr);
            PermissionsManager.updateLocalStorageState(PermissionsManager.KEY_PERMISSIONS_LAST_UPDATED_AT, (new Date()).toString());
        }
        Logger.info(TAG, "User permissions updated");
    }
    static getIntersectionWithDirectPermissions(permissions) {
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }
        const currentPermissions = PermissionsManager.getCurrentPermissions();
        if (!currentPermissions.direct) {
            Logger.warn(TAG, "The user does not have any direct permissions");
            return new Set();
        }
        const requiredPermissionsSet = new Set(permissions);
        const availablePermissionsSet = new Set(currentPermissions.direct);
        return new Set([...requiredPermissionsSet].filter(x => availablePermissionsSet.has(x)));
    }
}
PermissionsManager.PERMISSIONS_UPDATE_INTERVAL_MILLIS = 5 * 60 * 1000;
PermissionsManager.PERMISSION_REQUEST_INTERVAL_MILLIS = 3 * 60 * 1000;
PermissionsManager.KEY_PERMISSIONS_LAST_UPDATED_AT = 'al_permissions_last_updated_at';
PermissionsManager.KEY_PERMISSIONS_LAST_REQUESTED_AT = 'al_permissions_last_requested_at';
PermissionsManager.KEY_PERMISSIONS = 'al_permissions';
PermissionsManager.VIEW_MEDIATION_AD_UNITS_SDK_INFO = 'view_mediation_ad_units_sdk_info';
PermissionsManager.ADMIN_VIEW_MEDIATION_AD_UNITS = 'admin_view_mediation_ad_units';
PermissionsManager.EDIT_MEDIATION_AD_UNITS = 'edit_mediation_ad_units';
PermissionsManager.CONTENT_PUBLISHER_VIEW_MEDIATION_AD_UNITS = 'content_publisher_view_mediation_ad_units';
PermissionsManager.VIEW_OWNED_OPERATED_ACCOUNTS_SELF = 'view_owned_operated_accounts_self';
PermissionsManager.VIEW_OWNED_OPERATED_ACCOUNTS_PUB_DEV = 'view_owned_operated_accounts_pub_dev';
PermissionsManager.VIEW_OWNED_OPERATED_ACCOUNTS_PARENT = 'view_owned_operated_accounts_parent';
// permission to check whether the user is a mpa/ sub mpa/ pub dev/ sub pub dev
// this permission is also present for super admins and admins but does not affect this flow
// as they will need to super as someone to use the flow anyways.
PermissionsManager.IS_MPA_DERIVED_ACCOUNT = 'bypass_max_onboarding';
PermissionsManager.throttledPermissionScheduler = (schedulePermissionRefreshFn) => {
    const permissionsLastRequestedAtStr = typeof (localStorage) !== 'undefined' ? localStorage.getItem(PermissionsManager.KEY_PERMISSIONS_LAST_REQUESTED_AT) : 0;
    if (permissionsLastRequestedAtStr) {
        const permissionsLastRequestedAt = Date.parse(permissionsLastRequestedAtStr);
        const now = new Date();
        if ((now.getTime() - permissionsLastRequestedAt) < PermissionsManager.PERMISSION_REQUEST_INTERVAL_MILLIS) {
            // No need to request permissions, throttle it.
            return;
        }
    }
    PermissionsManager.updateLocalStorageState(PermissionsManager.KEY_PERMISSIONS_LAST_REQUESTED_AT, (new Date()).toString());
    schedulePermissionRefreshFn();
};
PermissionsManager.updateLocalStorageState = (key, state) => {
    if (typeof (localStorage) !== 'undefined') {
        localStorage.setItem(key, state);
    }
};
//# sourceMappingURL=PermissionsManager.js.map